'use client';

import * as React from 'react';
import { INVERTED_SUGAR_HIGH_STYLE } from '@/lib/syntax-highlight';
import { cn } from '@/lib/utils';
import { validateInteractiveSchema } from './schema-validation';
import type { MarkdownVariant } from './types';

// =============================================================================
// Lazy-loaded renderers
// =============================================================================

const LazyAIMermaidDiagram = React.lazy(
  () => import('@/components/organisms/ai/AIMermaidDiagram'),
);
const LazyAIAsciiDiagram = React.lazy(
  () => import('@/components/organisms/ai/AIAsciiDiagram'),
);
const LazyAIChart = React.lazy(
  () => import('@/components/organisms/ai/AIChart'),
);
const LazyAITable = React.lazy(
  () => import('@/components/organisms/ai/AITable'),
);
const LazyAIForm = React.lazy(() => import('@/components/organisms/ai/AIForm'));
const LazyAISelect = React.lazy(
  () => import('@/components/organisms/ai/AISelect'),
);
const LazyAICodeBlock = React.lazy(() =>
  import('@/components/organisms/ai/AICodeBlock').then((module) => ({
    default: module.AICodeBlock,
  })),
);

// =============================================================================
// Loading skeleton for lazy components
// =============================================================================

function LazyLoadingSkeleton() {
  return (
    <div className="my-4 flex items-center justify-center p-8 rounded-xl border border-border bg-muted animate-pulse motion-reduce:animate-none">
      <div className="h-4 w-32 bg-accent rounded" />
    </div>
  );
}

// =============================================================================
// Interactive languages (fire onInteraction events)
// =============================================================================

const INTERACTIVE_LANGUAGES = ['chart', 'select', 'table', 'form'] as const;
type InteractiveLanguage = (typeof INTERACTIVE_LANGUAGES)[number];

function isInteractiveLanguage(
  language: string,
): language is InteractiveLanguage {
  return INTERACTIVE_LANGUAGES.includes(language as InteractiveLanguage);
}

// =============================================================================
// Render-only languages (visual output, no interaction events)
// =============================================================================

const RENDER_ONLY_LANGUAGES = ['mermaid', 'ascii'] as const;
type RenderOnlyLanguage = (typeof RENDER_ONLY_LANGUAGES)[number];

function isRenderOnlyLanguage(
  language: string,
): language is RenderOnlyLanguage {
  return RENDER_ONLY_LANGUAGES.includes(language as RenderOnlyLanguage);
}

// =============================================================================
// Component lookup maps
// =============================================================================

type InteractiveComponentProps = {
  schema: unknown;
};

type CodeRendererEntry = {
  Component: React.LazyExoticComponent<React.ComponentType<{ code: string }>>;
  prop: 'code';
};

type ContentRendererEntry = {
  Component: React.LazyExoticComponent<
    React.ComponentType<{ content: string }>
  >;
  prop: 'content';
};

type RenderOnlyComponentEntry = CodeRendererEntry | ContentRendererEntry;

const INTERACTIVE_COMPONENTS: Record<
  InteractiveLanguage,
  React.LazyExoticComponent<React.ComponentType<InteractiveComponentProps>>
> = {
  chart: LazyAIChart as React.LazyExoticComponent<
    React.ComponentType<InteractiveComponentProps>
  >,
  select: LazyAISelect as React.LazyExoticComponent<
    React.ComponentType<InteractiveComponentProps>
  >,
  table: LazyAITable as React.LazyExoticComponent<
    React.ComponentType<InteractiveComponentProps>
  >,
  form: LazyAIForm as React.LazyExoticComponent<
    React.ComponentType<InteractiveComponentProps>
  >,
};

const RENDER_ONLY_COMPONENTS: Record<
  RenderOnlyLanguage,
  RenderOnlyComponentEntry
> = {
  mermaid: {
    Component: LazyAIMermaidDiagram as React.LazyExoticComponent<
      React.ComponentType<{ code: string }>
    >,
    prop: 'code',
  },
  ascii: {
    Component: LazyAIAsciiDiagram as React.LazyExoticComponent<
      React.ComponentType<{ content: string }>
    >,
    prop: 'content',
  },
};

const LOADING_FALLBACK = <LazyLoadingSkeleton />;

// =============================================================================
// Render helpers
// =============================================================================

function renderInteractiveComponent(
  language: InteractiveLanguage,
  schema: unknown,
): React.ReactElement {
  const Component = INTERACTIVE_COMPONENTS[language];
  return (
    <React.Suspense fallback={LOADING_FALLBACK}>
      <Component schema={schema} />
    </React.Suspense>
  );
}

function renderRenderOnlyComponent(
  language: RenderOnlyLanguage,
  content: string,
): React.ReactElement {
  const { Component, prop } = RENDER_ONLY_COMPONENTS[language];
  if (prop === 'code') {
    return (
      <React.Suspense fallback={LOADING_FALLBACK}>
        <Component code={content} />
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={LOADING_FALLBACK}>
      <Component content={content} />
    </React.Suspense>
  );
}

function renderInteractiveError({
  language,
  inverted,
  message,
}: {
  language: InteractiveLanguage;
  inverted: boolean;
  message: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'my-4 p-4 rounded-lg',
        inverted
          ? 'border border-primary-foreground/30 bg-background/10 text-primary-foreground text-sm'
          : 'border border-destructive bg-destructive/20 text-destructive text-sm',
      )}
      role="alert"
      data-slot="ai-interactive-error"
    >
      <span className="font-semibold">Invalid {language} schema:</span>{' '}
      {message}
    </div>
  );
}

function renderCodeBlock({
  code,
  language,
  inverted,
}: {
  code: string;
  language: string;
  inverted: boolean;
}): React.ReactElement {
  const block = (
    <React.Suspense fallback={LOADING_FALLBACK}>
      <LazyAICodeBlock
        code={code}
        language={language}
        showHeader={true}
        showCopyButton={true}
        className={inverted ? undefined : 'my-4'}
      />
    </React.Suspense>
  );

  if (!inverted) {
    return block;
  }

  return (
    <div
      className="my-4 [&_[data-slot=ai-code-block]]:bg-background/10 [&_[data-slot=ai-code-block]]:border-primary-foreground/20 [&_[data-slot=code-snippet-header]]:bg-background/5 [&_[data-slot=code-snippet-header]]:border-primary-foreground/20 [&_[data-slot=code-snippet-header]_span]:!text-primary-foreground/60 [&_[data-slot=code-snippet-header]_button]:!text-primary-foreground/60"
      style={INVERTED_SUGAR_HIGH_STYLE}
    >
      {block}
    </div>
  );
}

// =============================================================================
// Unified code component for react-markdown
// =============================================================================

function createCodeComponent(variant: MarkdownVariant = 'default'): React.FC<{
  className?: string;
  children?: React.ReactNode;
}> {
  const inv = variant === 'inverted';

  return function UnifiedCodeBlock({ className, children }) {
    const isInline = !className?.includes('language-');

    if (isInline) {
      return (
        <code
          className={cn(
            'px-1.5 py-0.5 rounded text-sm font-mono transition-colors',
            'break-words [overflow-wrap:anywhere]',
            inv
              ? 'bg-background/15 text-primary-foreground'
              : 'bg-accent text-foreground',
            className,
          )}
        >
          {children}
        </code>
      );
    }

    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'plaintext';
    const content = String(children).replace(/\n$/, '');

    if (isRenderOnlyLanguage(language)) {
      return renderRenderOnlyComponent(language, content);
    }

    if (isInteractiveLanguage(language)) {
      try {
        const schema = JSON.parse(content);
        const validation = validateInteractiveSchema(language, schema);

        if (!validation.valid) {
          return renderInteractiveError({
            language,
            inverted: inv,
            message:
              'The configuration does not match the expected schema shape.',
          });
        }

        return renderInteractiveComponent(language, validation.schema);
      } catch {
        return renderInteractiveError({
          language,
          inverted: inv,
          message:
            'Unable to parse the configuration. Please check the JSON syntax.',
        });
      }
    }

    return renderCodeBlock({ code: content, language, inverted: inv });
  };
}

export { createCodeComponent, LazyLoadingSkeleton };
